import { Link } from 'react-router'
import { SectionTitle } from '~/components/Sections'

const TIMELINE = [
  { year: 'Nov 2024', title: 'Incorporated', desc: 'S.J.S. Technology registered as a company in Bengaluru with a mission to bring premium digital printing solutions to Indian businesses.' },
  { year: 'Jan 2025', title: 'Official Inauguration', desc: 'Opened our doors with a fully equipped showroom, live machine demos, and partnerships with leading ink and media suppliers.' },
  { year: '2025', title: 'VICTORY Brand Launched', desc: 'Launched our own brand of heavy-duty printing machines — VICTORY — designed for winning performance in every print and every cut.' },
  { year: 'Today', title: 'Growing Across India', desc: 'Serving signage shops, textile printers, and packaging businesses across Karnataka and beyond with machines, inks, and 24/7 support.' },
]

const VALUES = [
  { icon: 'shield', title: 'Transparency', desc: 'Crystal-clear pricing and honest business policies in every transaction we make.' },
  { icon: 'zap', title: 'Performance', desc: 'VICTORY machines are built for heavy-duty production — precision and power in every print.' },
  { icon: 'users', title: 'Partnership', desc: 'We are more than a supplier. We grow with our clients and support them at every step.' },
  { icon: 'target', title: 'Innovation', desc: 'We regularly upgrade our offerings to stay ahead of industry requirements and technology.' },
]

function ValueIcon({ type }) {
  const props = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
  if (type === 'shield') return <svg {...props}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
  if (type === 'zap') return <svg {...props}><polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
  if (type === 'users') return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
  return <svg {...props}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
}

export default function AboutPage() {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero section-pad muted">
        <div className="container">
          <span className="eyebrow">About S.J.S. Technology</span>
          <h1 className="page-hero-title">More than a supplier — your growth partner</h1>
          <p className="page-hero-desc">
            Bengaluru-based and built on trust. We deliver premium digital printing
            machines, VisionJet inks, and complete after-sales support to help your
            business lead the market.
          </p>
        </div>
      </section>

      {/* Story — full width narrative */}
      <section className="section-pad">
        <div className="container">
          <div className="about-story">
            <div className="about-story-left">
              <SectionTitle
                eyebrow="Our Story"
                title="From vision to VICTORY"
              />
              <p>
                S.J.S. Technology was incorporated in November 2024 and officially
                inaugurated in January 2025 as a premier destination for digital
                printing and industrial solutions in Bengaluru.
              </p>
              <p>
                We realised that our clients needed machines that were not only
                technologically advanced but also rugged enough for heavy-duty
                production. That is why we launched <strong>VICTORY</strong> — our
                own brand and our promise of winning performance in every print
                and every cut.
              </p>
            </div>
            <div className="about-story-right">
              <div className="about-highlight-card">
                <span className="about-highlight-number">2024</span>
                <span className="about-highlight-label">Founded</span>
              </div>
              <div className="about-highlight-card">
                <span className="about-highlight-number">50+</span>
                <span className="about-highlight-label">Machines Installed</span>
              </div>
              <div className="about-highlight-card">
                <span className="about-highlight-number">100+</span>
                <span className="about-highlight-label">Happy Clients</span>
              </div>
              <div className="about-highlight-card">
                <span className="about-highlight-number">24/7</span>
                <span className="about-highlight-label">Service Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Approach — dark banner */}
      <section className="section-pad dark">
        <div className="container about-approach">
          <SectionTitle
            eyebrow="Our Approach"
            title="Moral business. Crystal-clear transparency."
          />
          <div className="about-approach-grid">
            <p>
              We follow moral business policies and crystal-pure transparency
              in all our transactions to maintain healthy, long-term
              relationships with every customer.
            </p>
            <p>
              Through our signature VICTORY brand and our partnership with
              VisionJet, we provide the precision and power your business
              needs to lead the market. We regularly upgrade our offerings
              to stay ahead of industry requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-pad muted">
        <div className="container">
          <SectionTitle
            eyebrow="What Drives Us"
            title="Core values behind every machine we sell"
          />
          <div className="card-grid cols-4">
            {VALUES.map((v) => (
              <article className="about-value-card" key={v.title}>
                <div className="about-value-icon">
                  <ValueIcon type={v.icon} />
                </div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-pad">
        <div className="container">
          <SectionTitle
            eyebrow="Our Journey"
            title="Key milestones"
          />
          <div className="about-timeline">
            {TIMELINE.map((item) => (
              <div className="about-timeline-item" key={item.year}>
                <div className="about-timeline-marker">
                  <span className="about-timeline-year">{item.year}</span>
                  <div className="about-timeline-dot" />
                </div>
                <div className="about-timeline-content">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad cta">
        <div className="container cta-box cta-box-split">
          <div>
            <span className="eyebrow">Ready to get started?</span>
            <h2>Visit our Bengaluru showroom for a live demo</h2>
            <p>
              See VICTORY machines in action, test print quality first-hand,
              and get a personalised recommendation for your business.
            </p>
          </div>
          <div className="cta-actions">
            <Link className="btn" to="/contact">Book a Demo</Link>
            <Link className="btn btn-ghost" to="/products">View Machines</Link>
          </div>
        </div>
      </section>
    </main>
  )
}
